/**
 * CURRENT vs PREVIOUS — the one copy of which cycles a line-item project picker
 * offers under each pill.
 *
 * ## What this is, and what it replaces
 *
 * Owner's request, 11 September 2026: *"the project list dropdown should show
 * current and previous pills with current ones shown by default in the dropdown
 * box, everywhere in all line items"*, clarified in the same breath —
 * ***"current means current and upcoming"***.
 *
 * ⚠️ **It LAYERS on `bc55979` rather than superseding it, and the difference is
 * the whole reason to read this paragraph.** That commit hard-wired the picker
 * to open cycles only — `useOpenCycleIds` / `projectCycleIsOpen` in
 * `use-line-project.ts` — and its measurement was that 36 of 68 active projects
 * sat in a finished cycle, so more than half of every line picker was history
 * nobody could sell against. **`current` here is byte-for-byte that same rule**,
 * and it stays the default, so nothing changes for anybody who does not touch
 * the new control.
 *
 * **What is genuinely new is that the other 36 are REACHABLE again.** `previous`
 * offers exactly the complement — the projects `bc55979` hid. That is a
 * behaviour change to what the picker offers, taken deliberately: the owner
 * asked for the pills knowing a closed-cycle project can be chosen through them.
 * It is not a loosening of any server rule, because there never was one — the
 * server refuses an inactive project and a project that does not sell the line's
 * product, and has no opinion whatever about the cycle.
 *
 * ## ⚠️ THIS NARROWS WHAT IS OFFERED. IT MUST NEVER NARROW WHAT IS RESOLVED
 *
 * Inherited verbatim from `bc55979`, and it is the property most easily broken
 * from here. 113 line items already point at a project whose cycle has closed
 * (31 quote, 82 invoice, measured on production 2026-09-09). A picker that
 * cannot show the value its own row holds renders blank over a real value, and
 * **the next save silently repoints the line** — a `200`, nothing on screen, and
 * on an invoice line the Xero tracking goes with it.
 *
 * So the grids keep fetching every active project and keep RESOLVING against
 * that full list, and filter only the array they turn into options — and the
 * saved project is injected back into the options of **every** scope, labelled,
 * by the block that already handles a since-retired one. The pills therefore
 * change what may be CHOSEN and never what may be DISPLAYED or SAVED.
 *
 * ## The test, written out so nobody "fixes" it into three scopes
 *
 * **A cycle is CURRENT when it has not ENDED: `endDate >= today`.** That single
 * comparison folds *running now* and *not started yet* into one scope, which is
 * exactly what the owner asked for. Written as two tests — one for "contains
 * today", one for "starts later" — it is one edit away from excluding the cycle
 * running today, and nothing on screen would show it.
 *
 * **PREVIOUS is the complement and is defined as the complement**, not as its
 * own predicate. Two independent predicates can both be false for the same row
 * — a cycle that belongs to neither pill is unreachable through either, which is
 * a project that has silently vanished from the app.
 *
 * **Bounds are INCLUSIVE.** A cycle's last day still belongs to it, so `>=` and
 * not `>`.
 *
 * ## Dates are compared as STRINGS, and that is not a shortcut
 *
 * `Cycle.startDate` and `Cycle.endDate` are plain `YYYY-MM-DD` with no timezone
 * — `types.ts` says so on the field: *do not pass to `new Date()`*. That format
 * sorts lexicographically, so a string compare is exact, where
 * `new Date('2026-05-31')` is midnight **UTC** and renders as 30 May west of
 * Greenwich.
 *
 * ⚠️ **{@link todayIso} uses LOCAL getters, and that is correct here rather than
 * the mirrored version of the same bug.** The trap this codebase warns about is
 * reading a bare `DATE` *from the API* through local getters. This is the
 * opposite direction: the question is which civil day it is **for the person at
 * the screen**, and `toISOString().slice(0, 10)` would answer it wrongly for
 * anybody east of Greenwich late in the evening. A cycle is a financial YEAR, so
 * a day of skew moves nothing except on the single midnight a cycle turns over,
 * and on that day either answer is defensible.
 */

import type { Cycle } from './types';

/**
 * Which half of the cycle calendar a project's dropdown is offering.
 *
 * Two members, and a third would need a decision rather than an addition: the
 * pair is defined as a PARTITION (see {@link cycleScopeOf}), so inserting a
 * scope between them means deciding which of these two loses the rows.
 */
export type ProjectCycleScope = 'current' | 'previous';

/**
 * ⚠️ **A `Record` over the union, never a ternary and never a `switch` with a
 * `default`.** A ternary is not an exhaustiveness check: this codebase has a
 * recorded case where a union grew and `tsc` found none of the mislabelling
 * sites, because every one of them had an `else` branch that silently claimed
 * the new member. A `Record` fails the build at each site instead.
 *
 * The labels are the WHOLE explanation the control gets. There is no hint, no
 * sentence under the pills and no tooltip — see the no-helper-text rule, which
 * these two words exist to satisfy rather than to sit beside.
 */
export const PROJECT_CYCLE_SCOPE_LABELS: Record<ProjectCycleScope, string> = {
  current: 'Current',
  previous: 'Previous',
};

/**
 * Every member, in the order they render. Derived from the label map's own keys
 * would lose the order guarantee, and the order here is meaningful — `current`
 * is the default and reads first.
 */
export const PROJECT_CYCLE_SCOPES: readonly ProjectCycleScope[] = ['current', 'previous'];

/**
 * ⚠️ **`current` on every mount, in every grid — owner's instruction.** It is
 * also what makes this change additive: `current` reproduces `bc55979`'s filter
 * exactly, so a person who never touches the pills sees the list they saw
 * yesterday.
 */
export const DEFAULT_PROJECT_CYCLE_SCOPE: ProjectCycleScope = 'current';

/** Today as `YYYY-MM-DD`, on the READER's clock. See the header for why local. */
export function todayIso(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Which pill a cycle belongs under.
 *
 * **The test is `endDate >= today`** — inclusive, string-compared, and one
 * comparison rather than two. See the header.
 *
 * `startDate` is in the signature and is deliberately unread: a cycle that has
 * not started yet is CURRENT ("current means current and upcoming"), so reading
 * it could only ever be a way of getting this wrong. It stays in the parameter
 * type so a caller passing a whole `Cycle` needs no adapter, and so the omission
 * is visible here rather than looking like a field somebody forgot existed.
 *
 * A missing or malformed `endDate` resolves to `current`. That is the safe
 * direction: `previous` is the pill nobody opens by default, so a row filed
 * there on the strength of a value we could not read would simply disappear.
 */
export function cycleScopeOf(
  cycle: Pick<Cycle, 'startDate' | 'endDate'>,
  today: string = todayIso(),
): ProjectCycleScope {
  if (typeof cycle.endDate !== 'string' || cycle.endDate === '') return 'current';
  return cycle.endDate >= today ? 'current' : 'previous';
}

/**
 * The ids of the cycles under one pill.
 *
 * ⚠️ **Both scopes must be built from the SAME `today`**, or a run that straddles
 * midnight can file one cycle under neither. Callers computing both should pass
 * one `today` to both calls — {@link cycleScopeIdIndex} does exactly that and is
 * the reason to prefer it.
 */
export function cycleIdsInScope(
  cycles: readonly Cycle[],
  scope: ProjectCycleScope,
  today: string = todayIso(),
): Set<number> {
  const ids = new Set<number>();
  for (const cycle of cycles) {
    if (cycleScopeOf(cycle, today) === scope) ids.add(cycle.id);
  }
  return ids;
}

/**
 * Both scopes' id sets, from one reading of "today".
 *
 * ⚠️ Each member is `Set<number> | null`, and **`null` is not an empty set** —
 * it means *the question could not be answered*, and every caller must read it
 * as **OFFER EVERYTHING**. See {@link projectInCycleScope}.
 */
export type CycleScopeIdIndex = Record<ProjectCycleScope, Set<number> | null>;

/** Every scope unanswerable. The shape a caller renders while `/cycles` is unread. */
export const UNKNOWN_CYCLE_SCOPES: CycleScopeIdIndex = { current: null, previous: null };

/**
 * Classify a whole cycle master into both pills at once, against one `today`.
 *
 * Returns {@link UNKNOWN_CYCLE_SCOPES} — every scope `null`, meaning *offer
 * everything* — when the master cannot classify anything. The caller decides
 * which conditions those are; this function only knows about the rows.
 */
export function cycleScopeIdIndex(
  cycles: readonly Cycle[],
  today: string = todayIso(),
): CycleScopeIdIndex {
  if (cycles.length === 0) return UNKNOWN_CYCLE_SCOPES;
  return {
    current: cycleIdsInScope(cycles, 'current', today),
    previous: cycleIdsInScope(cycles, 'previous', today),
  };
}

/**
 * Whether a project may be OFFERED under the pill currently selected.
 *
 * ⚠️ **Never use this to decide whether a project may be SAVED.** The server has
 * no opinion about a project's cycle, so a line already pointing at a closed one
 * saves perfectly well and must keep doing so. This is the direct successor of
 * `projectCycleIsOpen` in `use-line-project.ts` and carries that warning intact.
 *
 * Two shapes fall through as offerable, both deliberately:
 *
 *   - **`idsInScope === null`** — the cycles could not be read. Degrading to an
 *     empty list would empty every project dropdown in the app, and a picker
 *     with no options reads as *there are no projects*: a confident statement
 *     about the data, made on the strength of a request that never came back.
 *     Degrading OPEN restores exactly the behaviour that shipped before any of
 *     this existed, which is a longer list rather than an unusable one.
 *   - **the project names no cycle** — nothing to classify. `cycle_id` is
 *     `NOT NULL` in the schema so this should not arrive, and inventing a
 *     verdict for a shape that should not exist would hide the day it starts
 *     arriving.
 */
export function projectInCycleScope(
  project: { cycle: { id: number } | null },
  idsInScope: Set<number> | null,
): boolean {
  if (idsInScope === null) return true;
  if (project.cycle === null) return true;
  return idsInScope.has(project.cycle.id);
}

/**
 * Split a row's candidate projects into one option list per pill, with the
 * stored value pinned into **every** one of them.
 *
 * 🔴 **THE INJECTION INTO EVERY SCOPE IS THE WHOLE REASON THIS IS A SHARED
 * FUNCTION.** The single most likely way to break live data here is a pill that
 * hides the project a line already holds: the `Combobox` falls back to its
 * placeholder when `value` is absent from `options`, so the control renders
 * empty over a real stored project — and the next save, of any unrelated field,
 * silently repoints the line with a `200`. 113 live line items point at a
 * closed-cycle project. Three grids writing this partition by hand would agree
 * the day they were written and drift on the first correction, and the drift
 * shows up as **nothing at all** on screen.
 *
 * `storedOption` is what the row already holds, already shaped and already
 * labelled by the caller — it is pinned to the FRONT of any scope that does not
 * already list it, and left alone in the scope that does. So under `previous` a
 * historical project appears as itself, and under `current` it appears pinned
 * and labelled, and it is selectable and visible under both.
 *
 * `counts` are the projects genuinely OFFERED under each pill, **excluding the
 * pinned row** — that is the number {@link projectScopeFootnote} needs, and
 * counting the pin would suppress the sentence in the one case it exists for.
 */
export function projectOptionsByScope<
  P extends { cycle: { id: number } | null },
  O extends { value: string },
>(
  projects: readonly P[],
  scopes: CycleScopeIdIndex,
  toOption: (project: P) => O,
  storedOption: O | null,
): { options: Record<ProjectCycleScope, O[]>; counts: Record<ProjectCycleScope, number> } {
  const options = {} as Record<ProjectCycleScope, O[]>;
  const counts = {} as Record<ProjectCycleScope, number>;

  for (const scope of PROJECT_CYCLE_SCOPES) {
    const inScope = projects
      .filter((project) => projectInCycleScope(project, scopes[scope]))
      .map(toOption);
    counts[scope] = inScope.length;
    if (storedOption !== null && !inScope.some((option) => option.value === storedOption.value)) {
      inScope.unshift(storedOption);
    }
    options[scope] = inScope;
  }

  return { options, counts };
}

/**
 * The `Combobox` footnote for one pill — or `undefined` when there is nothing
 * to say.
 *
 * ⚠️ **"No projects" and "no projects in Previous" are different sentences, and
 * only the second tells the reader to press the other pill.** An empty dropdown
 * with no explanation reads as a failed fetch, which sends somebody hunting for
 * a fault that is not there — and this control is the first thing in the app
 * that can produce an empty list for a reason the reader can fix in one click.
 *
 * Said **only when another pill HAS some.** When the row has no projects at all
 * under any pill, the scope is not why, and the grids' existing "No active
 * project sells this product." wording stands alone rather than being
 * contradicted by a sentence implying the other pill is worth pressing.
 *
 * ⛔ **A FOOTNOTE IS A FACT ABOUT THE DATA, which is the documented exception to
 * the no-helper-text rule — the same standing as "Showing the first 100 of 400".
 * It is not advice and must never become any.** The two pill labels are the
 * whole explanation of what the control does.
 *
 * ⛔ **TRUNCATION IS DELIBERATELY NOT REPORTED HERE, and re-adding it would
 * reverse an owner decision.** The quote and invoice grids carried
 * `Showing the first N active projects — there are more.` on this exact picker
 * and **the owner removed it on 2026-09-05** in a stricter pass over footnotes
 * (see `listNote` in both grids, which is now a documented `undefined`). The
 * `hasMore` hazard is real and is unchanged by this control — the fetch is the
 * same single page of 100 active projects, so the scope split cannot move where
 * the cap bites — but saying so again is the owner's call to reverse, not ours.
 *
 * `counts` is per pill and is the count the picker genuinely OFFERS there,
 * excluding any pinned stored value. A project naming no cycle is offerable
 * under every pill and is therefore counted in each: the sentence claims what
 * this list holds, not how the cycle master is split.
 */
export function projectScopeFootnote(
  scope: ProjectCycleScope,
  counts: Record<ProjectCycleScope, number>,
): string | undefined {
  if (counts[scope] > 0) return undefined;
  /*
   * Built by walking the union rather than by naming "the other one". With two
   * members a ternary is the complement; with three it silently names the wrong
   * pill, and nothing fails. Filtering the whole list cannot.
   */
  const elsewhere = PROJECT_CYCLE_SCOPES.filter(
    (other) => other !== scope && counts[other] > 0,
  ).map((other) => `${counts[other]} in ${PROJECT_CYCLE_SCOPE_LABELS[other]}`);
  if (elsewhere.length === 0) return undefined;
  return `No projects in ${PROJECT_CYCLE_SCOPE_LABELS[scope]}. ${elsewhere.join(', ')}.`;
}
